import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Spin } from 'antd';

import { services as oidcServices } from '@nvision/identity-core';

import { ENV_VARS } from '@/env';
import { useNewDomainRedirect } from '@/hooks';
import { checkIdentityLogin, getUserLocalStorage, normalizeUserData, useIdentityAuth } from '@/modules/auth';
import { TIMEZONE_STORAGE_KEY } from '@/utils/date';

import { ACTION_NEEDED_ROUTES, API_APP_ROUTES, APP_ROUTES, AUTH_ROUTES } from '../../contants/routes';
import { HelpCenter } from '../../modules/help-center';
import { setLoginSuccess } from '../../redux/auth/AuthActions';
import { setAppLoader, setWarningModal } from '../../redux/common/CommonActions';
import PageBlockerModal from '../components/Modals/PageBlockerModal';
import WarningModal from '../components/Modals/WarningModal';
import ActionNeededRoutes from './ActionNeededRoutes';
import AppRoutes from './AppRoutes';

const RootRoutes = () => {
  useNewDomainRedirect();
  const isHBUser = ENV_VARS.REACT_APP_THEME === 'halliburton';
  const { ready: localesReady } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loginStatus = useSelector((state) => state.auth.user.loginStatus, shallowEqual);
  const appLoader = useSelector((state) => state.common.appLoader, shallowEqual);
  const warningModal = useSelector((state) => state.common.warningModal, shallowEqual);
  const isActionNeededRoute = String(location?.pathname).includes(ACTION_NEEDED_ROUTES.BASE);

  const { isIdentityLogin } = checkIdentityLogin();

  const auth = useIdentityAuth();

  const checkAuth = () => {
    const isLogged = checkLocalStore();
    if (isLogged || isIdentityLogin) {
      const loginData = getUserLocalStorage();
      if (location.pathname === AUTH_ROUTES.LOGIN) {
        navigate(API_APP_ROUTES[loginData?.userInfo?.landingMenuName] || APP_ROUTES.HOME);
      }
    } else {
      if (!Object.values(AUTH_ROUTES).includes(location.pathname) && !isActionNeededRoute) {
        navigate(AUTH_ROUTES.LOGIN);
      }
    }
  };

  const checkLocalStore = () => {
    const loginData = normalizeUserData(auth.user?.profile);
    if (loginData) {
      dispatch(setLoginSuccess({ data: loginData, tokenLogin: true }));
      return true;
    }
    return false;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO: fix
  useEffect(() => {
    checkAuth();
  }, [loginStatus, isActionNeededRoute]);

  const handleCloseWarningModal = (type) => {
    try {
      dispatch(setWarningModal({ description: '', open: false }));
      if (warningModal.callback) {
        warningModal.callback(type);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (auth.user) {
      // Setting identity data
      const normalizedUserData = normalizeUserData(auth.user.profile);
      dispatch(setLoginSuccess({ data: normalizedUserData }));
      localStorage.setItem('userInfo', JSON.stringify(normalizedUserData));
      localStorage.setItem(TIMEZONE_STORAGE_KEY, normalizedUserData?.pref?.timezone);
      dispatch(setAppLoader(false));
    }
  }, [auth.user, dispatch]);

  useEffect(() => {
    if (!auth.isLoading && !auth.user) {
      oidcServices.auth.enableOIDCProvider();
    }
  }, [auth.isLoading, auth.user]);

  return (
    <>
      <ToastContainer />
      {isActionNeededRoute ? <ActionNeededRoutes /> : <AppRoutes />}
      <div className={`app-loader ${appLoader || !localesReady ? 'visible' : 'not-visible'}`}>
        <Spin spinning size="large" color="red" />
      </div>
      <WarningModal
        open={warningModal?.open}
        description={warningModal?.description}
        onClose={() => handleCloseWarningModal('close')}
        onOk={() => handleCloseWarningModal('ok')}
      />
      <PageBlockerModal />
      <HelpCenter isHBUser={isHBUser} />
    </>
  );
};

export default RootRoutes;
